/**
 * =============================================================================
 * Componente: AnchorHelpPopover
 * Versão: 1.0.0
 *
 * Descrição:
 *   Popover contextual exibido ao lado do campo "Origem do prazo" na
 *   configuração de cada passo do Playbook. Mostra explicação curta sobre
 *   o que é a origem do prazo e lista APENAS as opções disponíveis no
 *   escopo atual (flat ou per_phase), para evitar a confusão de "o modal
 *   diz 4 opções mas eu só vejo 2".
 *
 * Release notes:
 *   v1.0.0 - Criação. Popover focado no campo, com link "Ver guia completo".
 * =============================================================================
 */
import { HelpCircle, Flag, Link2, CalendarPlus, LogIn } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface AnchorHelpPopoverProps {
  /** Escopo do playbook atual ('flat' = lista única / 'per_phase' = passos por fase). */
  scope: 'flat' | 'per_phase';
  /** Callback para abrir o guia completo (modal grande PlaybookHelpDialog). */
  onOpenFullHelp?: () => void;
}

// ----------------------------------------------------------------------------
// Catálogo das opções por escopo (mantém alinhamento com CrmPlaybookEditor)
// ----------------------------------------------------------------------------
const OPTIONS_FLAT = [
  {
    icon: Flag,
    title: 'Início da cadência',
    badge: 'cadence_start',
    desc: 'Conta a partir do momento em que a cadência foi disparada para o negócio.',
  },
  {
    icon: Link2,
    title: 'Após passo anterior',
    badge: 'previous_step_completed',
    desc: 'Conta a partir do instante em que o passo imediatamente anterior foi concluído.',
  },
  {
    icon: CalendarPlus,
    title: 'Criação do negócio',
    badge: 'deal_created',
    desc: 'Conta a partir da data em que o negócio foi criado no CRM.',
  },
];

const OPTIONS_PER_PHASE = [
  {
    icon: LogIn,
    title: 'Entrada na fase',
    badge: 'phase_entered',
    desc: 'Conta a partir do momento em que o negócio entrou nesta fase do pipeline.',
  },
  {
    icon: Link2,
    title: 'Após passo anterior',
    badge: 'previous_step_completed',
    desc: 'Conta a partir do instante em que o passo imediatamente anterior foi concluído.',
  },
];

export function AnchorHelpPopover({ scope, onOpenFullHelp }: AnchorHelpPopoverProps) {
  // Opções relevantes ao escopo atual — evita falar de opções que o select não mostra
  const options = scope === 'per_phase' ? OPTIONS_PER_PHASE : OPTIONS_FLAT;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5 text-muted-foreground hover:text-foreground"
          aria-label="O que é origem do prazo?"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-80 p-3">
        <div className="space-y-3">
          {/* Cabeçalho curto */}
          <div>
            <h4 className="text-sm font-semibold">Origem do prazo</h4>
            <p className="text-xs text-muted-foreground mt-1">
              Define a partir de qual momento o sistema começa a contar o prazo
              deste passo (ex.: "2 dias contados a partir de quando?").
            </p>
          </div>

          {/* Opções disponíveis no escopo atual */}
          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Opções disponíveis neste modo
            </p>
            <ul className="space-y-2">
              {options.map((opt) => {
                const Icon = opt.icon;
                return (
                  <li
                    key={opt.badge}
                    className="flex gap-2 rounded-md border bg-card p-2"
                  >
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary/10 text-primary">
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-medium text-foreground">
                          {opt.title}
                        </span>
                        <Badge
                          variant="outline"
                          className="font-mono text-[9px] px-1 py-0"
                        >
                          {opt.badge}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                        {opt.desc}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Link para o guia completo */}
          {onOpenFullHelp && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={onOpenFullHelp}
            >
              Ver guia completo do Playbook
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
